import { LifecyclePanel } from './lifecycle-panel.js';
import { ContactReferralsPanel } from './referrals-panel.js';
import { SendTimePanel } from './send-time-panel.js';
import { useQuery } from '@tanstack/react-query';
import { ContactPreferencesPanel } from './preferences-panel.js';
import { ContactCadencePanel } from './contact-cadence-panel.js';
import { WebsiteActivityPanel } from './website-activity-panel.js';
import { useParams } from 'react-router-dom';
import { api } from '@whinerp/client';
import { EmptyState, ErrorState, Skeleton } from '@whinerp/ui';
import { useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { ConsentEvidencePanel } from './consent-evidence-panel.js';
import { RecordPage, TimelineSkeleton } from './record-page.tsx';
import { SourcePanel } from './source-panel.js';

/**
 * SC-07 Contact record.
 *
 * The header renders first and the timeline streams in behind it, because the
 * header is what tells a person they opened the right record and making them
 * wait for a timeline query to find that out is the difference between a fast
 * product and a slow one.
 */

function ContactRecordScreen(): JSX.Element {
  const params = useParams();
  const contactId = params['id'] ?? '';

  const contact = useQuery({
    queryKey: ['contact', contactId],
    queryFn: () => api.getContact({ path: { contactId } }),
  });

  if (contact.isPending) return <ContactRecordLoading />;
  if (contact.isError) return <ContactRecordError error={contact.error} retry={() => void contact.refetch()} />;

  const detail = contact.data;

  return (
    <RecordPage
      objectKey="contact"
      recordId={contactId}
      title={detail.contact.fullName}
      subtitle={detail.contact.accountName ?? undefined}
      headerFieldKeys={['owner_id', 'title', 'updated_at']}
      consent={detail.consent}
      related={
        <>
          <SourcePanel objectKey="contact" recordId={contactId} />
        <WebsiteActivityPanel objectKey="contact" recordId={contactId} />
        <LifecyclePanel objectKey="contact" recordId={contactId} />
        <SendTimePanel objectKey="contact" recordId={contactId} />
        <ContactCadencePanel contactId={contactId} />
        <ContactReferralsPanel contactId={contactId} />
          {/* Preference beside consent, so the difference is visible. */}
          <ContactPreferencesPanel contactId={contactId} />
          <ConsentEvidencePanel objectKey="contact" recordId={contactId} />
        </>
      }
    />
  );
}

function ContactRecordEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="UserMinus"
      title={t('record.notFound')}
      body={t('record.notFound.body')}
      primary={{ label: t('object.contact.plural'), href: '/contacts', icon: 'Users' }}
    />
  );
}

function ContactRecordLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-5" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-line-h1 w-block-xl" />
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-record">
        <TimelineSkeleton />
        <Skeleton className="h-block-xl w-full" />
      </div>
    </div>
  );
}

function ContactRecordError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="This contact could not be loaded"
      body={error instanceof Error ? error.message : 'The record did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-07',
  route: '/contacts/:id',
  surface: 'page',
  title: 'Contact record',
  /*
   * F-032 is not claimed here, and the omission is the honest half.
   *
   * A note written on this record is a timeline entry of type 'note', which is
   * F-030 doing its job. F-032 is "notes, files and attachments": rich text,
   * mentions, upload, versioning, preview. The attachment table exists and no
   * code touches it, so claiming the feature from this screen would have made
   * an empty table read as a shipped capability.
   *
   * F-039: the consent chips are here and nowhere else in the internal app.
   * The preference centre (SC-37) is the customer's side of the same record.
   *
   * F-019: every record page carries Delete, and the undo that makes it safe.
   * The recycle-bin list has no screen id in 04_Screens, so it is not built
   * and not claimed - the ten seconds after the toast are where the
   * specification says restores actually happen.
   *
   * F-139: this is where capture happens. The queue itself is shell chrome,
   * like the toast - it belongs to no screen because it follows a person
   * across all of them - but the capability is delivered here, in the edit
   * that survives a lift.
   */
  // F-032: the note composer and the file drop zone both live on the record
  // page. Neither was declared anywhere, so a feature that has been built
  // since attachments shipped counted as having no reachable surface.
  // F-091: a save refused by a validation rule is shown on the field, rule named.
  features: ['F-027', 'F-030', 'F-032', 'F-039', 'F-139', 'F-156', 'F-012', 'F-014', 'F-018', 'F-019', 'F-091', 'F-079', 'F-082'],
  entitlement: 'F-027',
  reachedBy: [{ kind: 'screen', from: 'SC-06', how: 'the name in the contacts list' }],
  components: ['RecordHeader', 'Timeline', 'DetailsPanel', 'ConsentChips', 'FollowButton'],
  elements: ['RecordPage'],
  sampleRecord: { objectKey: 'contact', param: 'id' },
  states: {
    empty: ContactRecordEmpty,
    loading: ContactRecordLoading,
    error: ContactRecordError,
  },
  component: ContactRecordScreen,
});

export default screen;
